import { create } from 'zustand';
import { useApplicationStore } from './application';
import { parseISO, isValid } from 'date-fns';

interface ExpiredEffectiveDateState {
  isModalOpen: boolean;
  expiredEffectiveDate: string | null;
  isProcessing: boolean;
  error: string | null;

  setModalOpen: (isOpen: boolean) => void;
  setExpiredEffectiveDate: (date: string | null) => void;
  clearExpiredEffectiveDate: () => Promise<void>;
  checkExpiredEffectiveDate: () => Promise<boolean>;
}

export const useExpiredEffectiveDateStore = create<ExpiredEffectiveDateState>(
  (set) => ({
    isModalOpen: false,
    expiredEffectiveDate: null,
    isProcessing: false,
    error: null,

    setModalOpen: (isOpen: boolean) => {
      set({ isModalOpen: isOpen });
    },

    setExpiredEffectiveDate: (date: string | null) => {
      set({ expiredEffectiveDate: date });
    },

    clearExpiredEffectiveDate: async () => {
      set({ isProcessing: true, error: null });
      try {
        const { currentApplicationId, applications, updateApplicationById } =
          useApplicationStore.getState();
        const application = applications[0];
        if (!currentApplicationId || !application) {
          throw new Error('No application ID found');
        }

        await updateApplicationById(currentApplicationId, {
          ...application,
          phq: application.phq
            ? {
                ...application.phq,
                effectiveDate: null,
              }
            : application.phq,
        });
        await useApplicationStore.getState().updateTierFromApplicationState();
        await useApplicationStore.getState().getCurrentApplication();
        set({
          isModalOpen: false,
          expiredEffectiveDate: null,
          isProcessing: false,
        });
        window.location.reload();
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.details?.message ||
          'Failed to clear expired effective date';
        console.error('Failed to clear expired effective date:', error);
        set({ error: errorMessage, isProcessing: false });
        throw error;
      }
    },

    checkExpiredEffectiveDate: async () => {
      const { originalEffectiveDate, hasCompletedApplication } =
        useApplicationStore.getState();

      console.log('[ExpiredEffectiveDate] Checking:', {
        originalEffectiveDate,
        hasCompletedApplication,
      });

      if (hasCompletedApplication) {
        console.log(
          '[ExpiredEffectiveDate] Application already completed, skipping'
        );
        return false;
      }

      if (!originalEffectiveDate) {
        console.log(
          '[ExpiredEffectiveDate] No originalEffectiveDate, skipping'
        );
        return false;
      }

      const effectiveDate = originalEffectiveDate;

      try {
        const parsedEffectiveDate = parseISO(effectiveDate);
        if (!isValid(parsedEffectiveDate)) {
          console.log(
            '[ExpiredEffectiveDate] Invalid effectiveDate:',
            effectiveDate
          );
          return false;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        console.log('[ExpiredEffectiveDate] Comparing dates:', {
          effectiveDate: parsedEffectiveDate.toISOString(),
          today: today.toISOString(),
          isExpired: parsedEffectiveDate < today,
        });

        if (parsedEffectiveDate < today) {
          console.log('[ExpiredEffectiveDate] Date is expired, opening modal');
          set({
            isModalOpen: true,
            expiredEffectiveDate: effectiveDate,
          });
          return true;
        }
      } catch (error) {
        console.error('Failed to check expired effective date:', error);
      }

      return false;
    },
  })
);
